
import * as util from 'util'

import * as big from '../big'
import * as int64 from '../int64'
import * as uuid from '../uuid'

import * as fbe from '../fbe'
import * as core from '../core'
import * as entity from '../entity'

const Big = big.Big // eslint-disable-line
const Int64 = int64.Int64 // eslint-disable-line
const UInt64 = int64.UInt64 // eslint-disable-line
const UUID = uuid.UUID // eslint-disable-line

/**
 * TradeHistoryIdsResult struct
 */
class TradeHistoryIdsResult {
  /**
   * Initialize struct
   * @param {number=} lastRecordId
   * @param {!Array=} recordIds
   * @param {!boolean=} moreRecordsExist
   * @constructor
   */
  constructor (lastRecordId = null, recordIds = [], moreRecordsExist = false) {
    this.LastRecordId = lastRecordId
    this.RecordIds = recordIds
    this.MoreRecordsExist = moreRecordsExist
  }

  /**
   * Copy struct (shallow copy)
   * @this {!TradeHistoryIdsResult}
   * @param {!TradeHistoryIdsResult} other Other struct
   * @returns {!TradeHistoryIdsResult} This struct
   */
  copy (other) {
    if (other.LastRecordId != null) {
      this.LastRecordId = UInt64.fromNumber(other.LastRecordId)
    } else {
      this.LastRecordId = null
    }
    if (other.RecordIds != null) {
      this.RecordIds = []
      for (let item of other.RecordIds) {
        if (item != null) {
          let tempItem
          tempItem = UInt64.fromNumber(item)
          this.RecordIds.push(tempItem)
        } else {
          this.RecordIds.push(null)
        }
      }
    } else {
      this.RecordIds = null
    }
    if (other.MoreRecordsExist != null) {
      this.MoreRecordsExist = other.MoreRecordsExist
    } else {
      this.MoreRecordsExist = null
    }
    return this
  }

  /**
   * Clone struct (deep clone)
   * @this {!TradeHistoryIdsResult}
   * @returns {!TradeHistoryIdsResult} Cloned struct
   */
  clone () {
    // Serialize the struct to the FBE stream
    let writer = new TradeHistoryIdsResultModel(new fbe.WriteBuffer())
    writer.serialize(this)

    // Deserialize the struct from the FBE stream
    let reader = new TradeHistoryIdsResultModel(new fbe.ReadBuffer())
    reader.attachBuffer(writer.buffer)
    return reader.deserialize().value
  }

  /**
   * Is this struct equal to other one?
   * @this {!TradeHistoryIdsResult}
   * @param {!TradeHistoryIdsResult} other Other struct
   * @returns {boolean} Equal result
   */
  eq (other) {
    if (!(other instanceof TradeHistoryIdsResult)) {
      throw new TypeError('Instance of TradeHistoryIdsResult is required!')
    }
    return true
  }

  /**
   * Convert struct to JSON
   * @this {!TradeHistoryIdsResult}
   * @returns {!object} Struct value for JSON
   */
  toJSON () {
    return {
      LastRecordId: ((this.LastRecordId != null) ? this.LastRecordId.toNumber() : null),
      RecordIds: ((this.RecordIds != null) ? Array.from(this.RecordIds, item => ((item != null) ? item.toNumber() : null)) : null),
      MoreRecordsExist: ((this.MoreRecordsExist != null) ? this.MoreRecordsExist : null)
    }
  }

  /**
   * Convert JSON to struct
   * @param {!string} json JSON string
   * @returns {!object} Struct value for JSON
   */
  static fromJSON (json) {
    return TradeHistoryIdsResult.fromObject(JSON.parse(json))
  }

  /**
   * Create struct from object value
   * @param {!TradeHistoryIdsResult} other Object value
   * @returns {!TradeHistoryIdsResult} Created struct
   */
  static fromObject (other) {
    return new TradeHistoryIdsResult().copy(other)
  }

  /**
   * Get the FBE type
   * @this {!TradeHistoryIdsResult}
   * @returns {!number} FBE type
   */
  get fbeType () {
    return TradeHistoryIdsResult.fbeType
  }

  /**
   * Get the FBE type (static)
   * @this {!TradeHistoryIdsResult}
   * @returns {!number} FBE type
   */
  static get fbeType () {
    return 10054
  }
}

export { TradeHistoryIdsResult };

/**
 * Fast Binary Encoding TradeHistoryIdsResult field model
 */
class FieldModelTradeHistoryIdsResult extends fbe.FieldModel {
  /**
   * Initialize field model with the given buffer and offset
   * @param {!fbe.ReadBuffer|!fbe.WriteBuffer} buffer Buffer
   * @param {!number} offset Offset
   * @constructor
   */
  constructor (buffer, offset) {
    super(buffer, offset)
    this.LastRecordId = new fbe.FieldModelOptional(new fbe.FieldModelUInt64(buffer, 4 + 4), buffer, 4 + 4)
    this.RecordIds = new fbe.FieldModelVector(new fbe.FieldModelUInt64(buffer, this.LastRecordId.fbeOffset + this.LastRecordId.fbeSize), buffer, this.LastRecordId.fbeOffset + this.LastRecordId.fbeSize)
    this.MoreRecordsExist = new fbe.FieldModelBool(buffer, this.RecordIds.fbeOffset + this.RecordIds.fbeSize)
  }

  /**
   * Get the field size
   * @this {!FieldModelTradeHistoryIdsResult}
   * @returns {!number} Field size
   */
  get fbeSize () {
    return 4
  }

  /**
   * Get the field body size
   * @this {!FieldModelTradeHistoryIdsResult}
   * @returns {!number} Field body size
   */
  get fbeBody () {
    return 4 + 4 + this.LastRecordId.fbeSize + this.RecordIds.fbeSize + this.MoreRecordsExist.fbeSize
  }

  /**
   * Get the field extra size
   * @this {!FieldModelTradeHistoryIdsResult}
   * @returns {!number} Field extra size
   */
  get fbeExtra () {
    if ((this._buffer.offset + this.fbeOffset + this.fbeSize) > this._buffer.size) {
      return 0
    }

    let fbeStructOffset = this.readUInt32(this.fbeOffset)
    if ((fbeStructOffset === 0) || ((this._buffer.offset + fbeStructOffset + 4) > this._buffer.size)) {
      return 0
    }

    this._buffer.shift(fbeStructOffset)

    let fbeResult = this.fbeBody + this.LastRecordId.fbeExtra + this.RecordIds.fbeExtra + this.MoreRecordsExist.fbeExtra

    this._buffer.unshift(fbeStructOffset)

    return fbeResult
  }

  /**
   * Get the field type
   * @this {!FieldModelTradeHistoryIdsResult}
   * @returns {!number} Field type
   */
  get fbeType () {
    return FieldModelTradeHistoryIdsResult.fbeType
  }

  /**
   * Get the field type (static)
   * @this {!FieldModelTradeHistoryIdsResult}
   * @returns {!number} Field type
   */
  static get fbeType () {
    return 10054
  }

  /**
   * Check if the struct value is valid
   * @this {!FieldModelTradeHistoryIdsResult}
   * @param {!boolean} fbeVerifyType Verify model type flag, defaults is true
   * @returns {!boolean} Field model valid state
   */
  verify (fbeVerifyType = true) {
    if ((this._buffer.offset + this.fbeOffset + this.fbeSize) > this._buffer.size) {
      return true
    }

    let fbeStructOffset = this.readUInt32(this.fbeOffset)
    if ((fbeStructOffset === 0) || ((this._buffer.offset + fbeStructOffset + 4 + 4) > this._buffer.size)) {
      return false
    }

    let fbeStructSize = this.readUInt32(fbeStructOffset)
    if (fbeStructSize < (4 + 4)) {
      return false
    }

    let fbeStructType = this.readUInt32(fbeStructOffset + 4)
    if (fbeVerifyType && (fbeStructType !== this.fbeType)) {
      return false
    }

    this._buffer.shift(fbeStructOffset)
    let fbeResult = this.verifyFields(fbeStructSize)
    this._buffer.unshift(fbeStructOffset)
    return fbeResult
  }

  /**
   * Check if the struct fields are valid
   * @this {!FieldModelTradeHistoryIdsResult}
   * @param {!number} fbeStructSize FBE struct size
   * @returns {!boolean} Field model valid state
   */
  verifyFields (fbeStructSize) {
    let fbeCurrentSize = 4 + 4

    if ((fbeCurrentSize + this.LastRecordId.fbeSize) > fbeStructSize) {
      return true
    }
    if (!this.LastRecordId.verify()) {
      return false
    }
    // noinspection JSUnusedAssignment
    fbeCurrentSize += this.LastRecordId.fbeSize

    if ((fbeCurrentSize + this.RecordIds.fbeSize) > fbeStructSize) {
      return true
    }
    if (!this.RecordIds.verify()) {
      return false
    }
    // noinspection JSUnusedAssignment
    fbeCurrentSize += this.RecordIds.fbeSize

    if ((fbeCurrentSize + this.MoreRecordsExist.fbeSize) > fbeStructSize) {
      return true
    }
    if (!this.MoreRecordsExist.verify()) {
      return false
    }
    // noinspection JSUnusedAssignment
    fbeCurrentSize += this.MoreRecordsExist.fbeSize

    return true
  }

  /**
   * Get the struct value (begin phase)
   * @this {!FieldModelTradeHistoryIdsResult}
   * @returns {!number} Field model begin offset
   */
  getBegin () {
    if ((this._buffer.offset + this.fbeOffset + this.fbeSize) > this._buffer.size) {
      return 0
    }

    let fbeStructOffset = this.readUInt32(this.fbeOffset)
    console.assert((fbeStructOffset > 0) && ((this._buffer.offset + fbeStructOffset + 4 + 4) <= this._buffer.size), 'Model is broken!')
    if ((fbeStructOffset === 0) || ((this._buffer.offset + fbeStructOffset + 4 + 4) > this._buffer.size)) {
      return 0
    }

    let fbeStructSize = this.readUInt32(fbeStructOffset)
    console.assert((fbeStructSize >= (4 + 4)), 'Model is broken!')
    if (fbeStructSize < (4 + 4)) {
      return 0
    }

    this._buffer.shift(fbeStructOffset)
    return fbeStructOffset
  }

  /**
   * Get the struct value (end phase)
   * @this {!FieldModelTradeHistoryIdsResult}
   * @param {!number} fbeBegin Field model begin offset
   */
  getEnd (fbeBegin) {
    this._buffer.unshift(fbeBegin)
  }

  /**
   * Get the struct value
   * @this {!FieldModelTradeHistoryIdsResult}
   * @param {!TradeHistoryIdsResult} fbeValue Default value, defaults is new TradeHistoryIdsResult()
   * @returns {!TradeHistoryIdsResult} TradeHistoryIdsResult value
   */
  get (fbeValue = new TradeHistoryIdsResult()) {
    let fbeBegin = this.getBegin()
    if (fbeBegin === 0) {
      return fbeValue
    }

    let fbeStructSize = this.readUInt32(0)
    this.getFields(fbeValue, fbeStructSize)
    this.getEnd(fbeBegin)
    return fbeValue
  }

  /**
   * Get the struct fields values
   * @this {!FieldModelTradeHistoryIdsResult}
   * @param {!TradeHistoryIdsResult} fbeValue TradeHistoryIdsResult value
   * @param {!number} fbeStructSize Struct size
   */
  getFields (fbeValue, fbeStructSize) {
    let fbeCurrentSize = 4 + 4

    if ((fbeCurrentSize + this.LastRecordId.fbeSize) <= fbeStructSize) {
      fbeValue.LastRecordId = this.LastRecordId.get()
    } else {
      fbeValue.LastRecordId = null
    }
    // noinspection JSUnusedAssignment
    fbeCurrentSize += this.LastRecordId.fbeSize

    if ((fbeCurrentSize + this.RecordIds.fbeSize) <= fbeStructSize) {
      this.RecordIds.get(fbeValue.RecordIds)
    } else {
      fbeValue.RecordIds.length = 0
    }
    // noinspection JSUnusedAssignment
    fbeCurrentSize += this.RecordIds.fbeSize

    if ((fbeCurrentSize + this.MoreRecordsExist.fbeSize) <= fbeStructSize) {
      fbeValue.MoreRecordsExist = this.MoreRecordsExist.get(false)
    } else {
      fbeValue.MoreRecordsExist = false
    }
    // noinspection JSUnusedAssignment
    fbeCurrentSize += this.MoreRecordsExist.fbeSize
  }

  /**
   * Set the struct value (begin phase)
   * @this {!FieldModelTradeHistoryIdsResult}
   * @returns {!number} Field model begin offset
   */
  setBegin () {
    console.assert(((this._buffer.offset + this.fbeOffset + this.fbeSize) <= this._buffer.size), 'Model is broken!')
    if ((this._buffer.offset + this.fbeOffset + this.fbeSize) > this._buffer.size) {
      return 0
    }

    let fbeStructSize = this.fbeBody
    let fbeStructOffset = this._buffer.allocate(fbeStructSize) - this._buffer.offset
    console.assert((fbeStructOffset > 0) && ((this._buffer.offset + fbeStructOffset + fbeStructSize) <= this._buffer.size), 'Model is broken!')
    if ((fbeStructOffset <= 0) || ((this._buffer.offset + fbeStructOffset + fbeStructSize) > this._buffer.size)) {
      return 0
    }

    this.writeUInt32(this.fbeOffset, fbeStructOffset)
    this.writeUInt32(fbeStructOffset, fbeStructSize)
    this.writeUInt32(fbeStructOffset + 4, this.fbeType)

    this._buffer.shift(fbeStructOffset)
    return fbeStructOffset
  }

  /**
   * Set the struct value (end phase)
   * @this {!FieldModelTradeHistoryIdsResult}
   * @param {!number} fbeBegin Field model begin offset
   */
  setEnd (fbeBegin) {
    this._buffer.unshift(fbeBegin)
  }

  /**
   * Set the struct value
   * @this {!FieldModelTradeHistoryIdsResult}
   * @param {!TradeHistoryIdsResult} fbeValue TradeHistoryIdsResult value
   */
  set (fbeValue) {
    let fbeBegin = this.setBegin()
    if (fbeBegin === 0) {
      return
    }

    this.setFields(fbeValue)
    this.setEnd(fbeBegin)
  }

  /**
   * Set the struct fields values
   * @this {!FieldModelTradeHistoryIdsResult}
   * @param {TradeHistoryIdsResult} fbeValue TradeHistoryIdsResult value
   */
  setFields (fbeValue) {
    this.LastRecordId.set(fbeValue.LastRecordId)
    this.RecordIds.set(fbeValue.RecordIds)
    this.MoreRecordsExist.set(fbeValue.MoreRecordsExist)
  }
}

export { FieldModelTradeHistoryIdsResult };

/**
 * Fast Binary Encoding TradeHistoryIdsResult model
 */
class TradeHistoryIdsResultModel extends fbe.Model {
  /**
   * Initialize model with the given buffer
   * @param {!fbe.ReadBuffer|!fbe.WriteBuffer} buffer Read/Write buffer, defaults is new fbe.WriteBuffer()
   * @constructor
   */
  constructor (buffer = new fbe.WriteBuffer()) {
    super(buffer)
    this._model = new FieldModelTradeHistoryIdsResult(this.buffer, 4)
  }

  /**
   * Get the TradeHistoryIdsResult model
   * @this {!TradeHistoryIdsResultModel}
   * @returns {!FieldModelTradeHistoryIdsResult} model TradeHistoryIdsResult model
   */
  get model () {
    return this._model
  }

  /**
   * Get the model size
   * @this {!TradeHistoryIdsResultModel}
   * @returns {!number} Model size
   */
  get fbeSize () {
    return this.model.fbeSize + this.model.fbeExtra
  }

  /**
   * Get the model type
   * @this {!TradeHistoryIdsResultModel}
   * @returns {!number} Model type
   */
  get fbeType () {
    return TradeHistoryIdsResultModel.fbeType
  }

  /**
   * Get the model type (static)
   * @this {!TradeHistoryIdsResultModel}
   * @returns {!number} Model type
   */
  static get fbeType () {
    return FieldModelTradeHistoryIdsResult.fbeType
  }

  /**
   * Check if the struct value is valid
   * @this {!TradeHistoryIdsResultModel}
   * @returns {!boolean} Model valid state
   */
  verify () {
    if ((this.buffer.offset + this.model.fbeOffset - 4) > this.buffer.size) {
      return false
    }

    let fbeFullSize = this.readUInt32(this.model.fbeOffset - 4)
    if (fbeFullSize < this.model.fbeSize) {
      return false
    }

    return this.model.verify()
  }

  /**
   * Create a new model (begin phase)
   * @this {!TradeHistoryIdsResultModel}
   * @returns {!number} Model begin offset
   */
  createBegin () {
    return this.buffer.allocate(4 + this.model.fbeSize)
  }

  /**
   * Create a new model (end phase)
   * @this {!TradeHistoryIdsResultModel}
   * @param {!number} fbeBegin Model begin offset
   */
  createEnd (fbeBegin) {
    let fbeEnd = this.buffer.size
    let fbeFullSize = fbeEnd - fbeBegin
    this.writeUInt32(this.model.fbeOffset - 4, fbeFullSize)
    return fbeFullSize
  }

  /**
   * Serialize the struct value
   * @this {!TradeHistoryIdsResultModel}
   * @param {!TradeHistoryIdsResult} value TradeHistoryIdsResult value
   * @return {!number} Model begin offset
   */
  serialize (value) {
    let fbeBegin = this.createBegin()
    this.model.set(value)
    return this.createEnd(fbeBegin)
  }

  /**
   * Deserialize the struct value
   * @this {!TradeHistoryIdsResultModel}
   * @param {!TradeHistoryIdsResult} value TradeHistoryIdsResult value, defaults is new TradeHistoryIdsResult()
   * @return {!object} Deserialized TradeHistoryIdsResult value and its size
   */
  deserialize (value = new TradeHistoryIdsResult()) {
    if ((this.buffer.offset + this.model.fbeOffset - 4) > this.buffer.size) {
      return { value: new TradeHistoryIdsResult(), size: 0 }
    }

    let fbeFullSize = this.readUInt32(this.model.fbeOffset - 4)
    console.assert((fbeFullSize >= this.model.fbeSize), 'Model is broken!')
    if (fbeFullSize < this.model.fbeSize) {
      return { value: new TradeHistoryIdsResult(), size: 0 }
    }

    this.model.get(value)
    return { value: value, size: fbeFullSize }
  }

  /**
   * Move to the next struct value
   * @this {!TradeHistoryIdsResultModel}
   * @param {!number} prev Previous TradeHistoryIdsResult model size
   */
  next (prev) {
    this.model.fbeShift(prev)
  }
}

export { TradeHistoryIdsResultModel };
